import { convert, detect, type Encoding } from 'encoding-japanese'
import { parse } from 'papaparse'

import { type EffectiveTable, type ParsedCsv } from '../models'
import { type CsvReadConfig } from '../models/csv-import-state'

/**
 * CSV取り込みの失敗種別。UI はこの kind に応じてメッセージを出し分ける。
 * - too-large: ファイルサイズ上限超過
 * - parse-failed: papaparse のパースエラー（壊れたCSV・不正な引用符等）
 */
export type CsvImportErrorKind = 'too-large' | 'parse-failed'

export class CsvImportError extends Error {
  constructor(readonly kind: CsvImportErrorKind) {
    super(kind)
    this.name = 'CsvImportError'
  }
}

/**
 * FE のメモリ内パースの上限（20MB）。
 * S3 アップロード用の MAX_UPLOAD_FILE_SIZE（500MB）と異なり、ブラウザで全量を
 * arrayBuffer 読み込み＋パースするため、フリーズ防止に厳しめの上限を設ける。
 */
export const MAX_CSV_FILE_SIZE = 20 * 1024 * 1024

/** encoding-japanese の検出ラベル → 表示用ラベル。 */
const DISPLAY_LABEL: Partial<Record<Encoding, string>> = {
  UTF8: 'UTF-8',
  SJIS: 'Shift_JIS',
  EUCJP: 'EUC-JP',
  UNICODE: 'UTF-16',
  ASCII: 'ASCII'
}

/** ユーザーが手動で選べる文字コード（自動判定の誤りを上書きする用）。 */
export const SELECTABLE_ENCODINGS = ['UTF8', 'SJIS', 'EUCJP'] as const
export type SelectableEncoding = (typeof SELECTABLE_ENCODINGS)[number]
/** 文字コードの指定。'auto' は encoding-japanese による自動判定。 */
export type EncodingOverride = 'auto' | SelectableEncoding

/** 文字コードラベル（表示用）。未知は元ラベルをそのまま返す。 */
export const encodingDisplayLabel = (enc: Encoding): string =>
  DISPLAY_LABEL[enc] ?? enc

/** バイト列を指定の文字コードで Unicode 文字列にデコードする。 */
export const decodeBytes = (bytes: Uint8Array, from: Encoding): string =>
  convert(bytes, { to: 'UNICODE', from, type: 'string' })

const UTF8_BOM = [0xef, 0xbb, 0xbf] as const

const hasUtf8Bom = (bytes: Uint8Array): boolean =>
  UTF8_BOM.every((byte, i) => bytes[i] === byte)

const hasNonAscii = (bytes: Uint8Array): boolean =>
  bytes.some((byte) => byte >= 0x80)

/**
 * バイト列の文字コードを判定する。サーバーの CSV 取込（encode-stream.ts の judgeEncoding）と同じく
 * UTF-8 → Shift_JIS → 自動判定の順で見る。
 * 1. UTF-8: BOM 付き、または非 ASCII を含み UTF-8 として矛盾しない
 * 2. Shift_JIS: 非 ASCII を含み Shift_JIS（CP932）として矛盾せず、EUC-JP としては読めない
 * 3. 自動判定: 上のどちらにも当たらない → encoding-japanese の detect（検出不能は UTF8）
 *
 * UTF-8 を先に見るのは、日本語の UTF-8 は Shift_JIS としても読めることがあるが、
 * Shift_JIS が UTF-8 として矛盾なく読めることは実質ないため。
 * Shift_JIS と EUC-JP の両方として読めるバイト列（半角カナだけの行など）はどちらとも言い切れないので、
 * 自動判定（EUC-JP を先に見る）に任せ、誤りは画面の文字コード選択で直してもらう。
 */
const judgeEncoding = (bytes: Uint8Array): Encoding => {
  if (hasUtf8Bom(bytes)) return 'UTF8'
  if (hasNonAscii(bytes)) {
    if (detect(bytes, 'UTF8')) return 'UTF8'
    if (detect(bytes, 'SJIS') && !detect(bytes, 'EUCJP')) return 'SJIS'
  }
  return detect(bytes) || 'UTF8'
}

/**
 * バイト列の文字コードを判定する（判定の順は judgeEncoding）。
 * 返り値: from = encoding-japanese の変換元ラベル、display = 表示用ラベル。
 * 誤判定時は parseCsvFile の forcedEncoding で手動上書きできる（ステップ1の文字コード選択）。
 */
export const detectEncoding = (
  bytes: Uint8Array
): { from: Encoding; display: string } => {
  const from = judgeEncoding(bytes)
  return { from, display: DISPLAY_LABEL[from] ?? from }
}

/**
 * CSVテキストを2次元配列にパースする（papaparse・同期）。
 * - header: false で固定（位置で列を識別するため。ヘッダー有無はステップ1のUIで扱う）。
 * - skipEmptyLines: 'greedy' で完全空行を除去（末尾の空行ノイズを避ける）。
 * - 呼び出し側で文字コード判定・デコード済み（encoding-japanese）のテキストを渡す。
 */
/**
 * Excel 由来の =\"...\" 囲み（先頭ゼロ保持のためのテキスト強制記法）を剥がす。
 * サントリー実データに =\"0163252966\" 形式が頻出するため（既存サントリーパーサーの
 * formatCode と同じ正規表現）。汎用取込は任意列に任意値が来るので、列を特定せず
 * 全セルの正規化として適用する。
 */
const unwrapExcelText = (cell: string): string => {
  const match = cell.match(/^="(.+)"$/)
  return match ? match[1] : cell
}

export const parseCsvText = (csvText: string): string[][] => {
  // 先頭の BOM（Excel の「CSV UTF-8」保存で付く）を取り除く。残すと 1 列目のヘッダー名が
  // 「\uFEFF外部キー」になり、通常フォーマットの列として認識できない（サーバー側の読み込みも BOM を除いて読む）
  const text = csvText.charCodeAt(0) === 0xfeff ? csvText.slice(1) : csvText
  const result = parse<string[]>(text, {
    header: false,
    skipEmptyLines: 'greedy'
  })
  // パースエラー（不正な引用符・区切り検出失敗等）があれば失敗として扱い、
  // UI 側の catch でエラー表示に遷移させる（握り潰さない）。
  if (result.errors.length > 0) {
    throw new CsvImportError('parse-failed')
  }
  // 各セルを文字列に正規化（papaparse は通常 string を返すが、念のため）し、
  // Excel 由来の =\"...\" 囲みを剥がす
  return result.data.map((row) =>
    row.map((cell) => unwrapExcelText(cell == null ? '' : String(cell)))
  )
}

/**
 * File からバイト列を読み、文字コードを判定（encoding-japanese）してデコードし、パースする。
 * S3 等にアップロードせず、ブラウザのメモリ上で完結する。UTF-8 / Shift-JIS 等に対応。
 * forcedEncoding を渡すと自動判定をスキップしてその文字コードでデコードする（誤判定の手動上書き）。
 */
export const parseCsvFile = async (
  file: File,
  forcedEncoding?: Encoding
): Promise<ParsedCsv> => {
  // メモリ内パースの上限超過は読み込み前に弾く（巨大CSVのフリーズ防止）。
  if (file.size > MAX_CSV_FILE_SIZE) {
    throw new CsvImportError('too-large')
  }
  const bytes = new Uint8Array(await file.arrayBuffer())
  const { from, display } = forcedEncoding
    ? { from: forcedEncoding, display: encodingDisplayLabel(forcedEncoding) }
    : detectEncoding(bytes)
  const text = decodeBytes(bytes, from)
  const rows = parseCsvText(text)
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
  return {
    fileName: file.name,
    rows,
    rowCount: rows.length,
    columnCount,
    encoding: display,
    encodingCode: from
  }
}

/**
 * 開始行・開始列の設定を反映した「実効テーブル」を組み立てる純関数。
 * - ヘッダー行（hasHeader のとき）・データ開始行・開始列をすべて適用する。
 * - 開始列より前の列、開始行より前の行は読み取り対象から除外する。
 * - 行・列番号は 1 始まり。配列インデックスへは -1 して使う。
 *
 * step2 以降のマッピングは、この EffectiveTable の headers / columnCount を参照する。
 */
export const buildEffectiveTable = (
  parsed: ParsedCsv,
  config: CsvReadConfig
): EffectiveTable => {
  const { rows, columnCount: rawColumnCount } = parsed
  const { hasHeader, headerRowNo, startRowNo, startColNo } = config

  // 開始列インデックス（0始まり）。1未満や範囲外は 0 にクランプ。
  const startColIdx = Math.max(0, startColNo - 1)
  // 実効列数 = 生の最大列数 - 開始列より前の列数（負にならないようクランプ）
  const columnCount = Math.max(0, rawColumnCount - startColIdx)

  // 指定行（1始まり）の、開始列以降のセル配列を取り出すヘルパー
  const sliceRow = (rowNo: number): string[] => {
    const row = rows[rowNo - 1] ?? []
    const sliced = row.slice(startColIdx)
    // 実効列数に満たない行は空文字で埋めて列数を揃える
    return Array.from({ length: columnCount }, (_, i) => sliced[i] ?? '')
  }

  const headers = hasHeader ? sliceRow(headerRowNo) : []

  // データ開始行（startRowNo）以降の全行
  const dataRows: string[][] = []
  for (let rowNo = startRowNo; rowNo <= rows.length; rowNo++) {
    dataRows.push(sliceRow(rowNo))
  }

  return { headers, dataRows, columnCount }
}

/**
 * CSV ファイルを、セルを加工せずに読む（=\"...\" の囲みも外さない）。文字コードは自動判定。
 * サントリー様の修正を反映した CSV を元の CSV と同じ形で書き出すときに、元のセルの形（囲みの有無）を残すために使う。
 */
export const readCsvFileRaw = async (
  file: File
): Promise<{ rows: string[][]; encodingCode: Encoding }> => {
  const bytes = new Uint8Array(await file.arrayBuffer())
  const { from } = detectEncoding(bytes)
  const text = decodeBytes(bytes, from)
  const body = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
  const result = parse<string[]>(body, {
    header: false,
    skipEmptyLines: 'greedy'
  })
  if (result.errors.length > 0) {
    throw new CsvImportError('parse-failed')
  }
  return {
    rows: result.data.map((row) =>
      row.map((cell) => (cell == null ? '' : String(cell)))
    ),
    encodingCode: from
  }
}
